/* eslint-disable @typescript-eslint/naming-convention */
import {
  type BaseProductUpdateRequestBody,
  type ProductImageDeleteBody,
  type ProductResponseBody,
  type ProductUpdateRequestBody,
  type SecondaryImage,
} from 'legacy/routes/catalog/types/product.types'
import instacart from 'legacy/common/instacart'
import { keysOfObject } from 'legacy/common/utils'
import { errors } from 'utils/error-handling/errors'
import {
  type EditImagesValues,
  type SecondaryImageAngleMapping,
} from './types/EditImageTypes.types'

function serializeSecondaryImages(secondaryImages: SecondaryImageAngleMapping) {
  return keysOfObject(secondaryImages).reduce((acc: SecondaryImage[], angle) => {
    const angleUrl = secondaryImages[angle]
    if (!angleUrl) return acc

    return [...acc, { angle, primary_url: angleUrl }]
  }, [])
}

const getImageUpdateRequest = (
  data: ProductResponseBody,
  productImages: EditImagesValues,
  values: EditImagesValues
): ProductUpdateRequestBody => {
  let request: ProductUpdateRequestBody = {
    editable: data.editable,
    is_private_label: data.is_private_label,
    details: data.details || '',
  }
  if (values && values.primaryImageUrl && values.primaryImageUrl != productImages.primaryImageUrl) {
    request = {
      ...request,
      product_image: { remote_image_url: values.primaryImageUrl },
    }
  }
  if (values && Object.keys(values.secondaryImages).length > 0) {
    request = {
      ...request,
      secondary_images: serializeSecondaryImages(values.secondaryImages),
    }
  }
  return addEditableOrRetailerOwnedProperties(data, request)
}

const addEditableOrRetailerOwnedProperties = (
  data: ProductResponseBody,
  requestBody: BaseProductUpdateRequestBody
) => {
  const { is_private_label, brand_id, brand_name, size, name, unit_count, editable } = data

  // Owning a product means the brand should be updated through the dropdown
  // i.e. sending an id from a pre-fetched list.
  if (editable && is_private_label) {
    return {
      ...requestBody,
      editable: true,
      is_private_label: true,
      brand_id,
      name,
      size,
      unit_count,
    }
  }

  // Not owning a product (with the editable flag true) means
  // this can be edited as free text, and we should not send brand_id.
  if (editable && !is_private_label) {
    return {
      ...requestBody,
      editable: true,
      is_private_label: false,
      brand_name,
      name,
      size,
      unit_count,
    }
  }

  // If neither, don't add extra fields at all.
  return requestBody
}

const extractDeletedImageAnglesFromValues = (
  productImages: EditImagesValues,
  values: EditImagesValues
) =>
  Object.keys(productImages.secondaryImages)
    .map(angle => {
      if (productImages.secondaryImages[angle] && !values.secondaryImages[angle]) {
        return angle
      }
      return null
    })
    .filter(a => a)

export const requestUpdate = async (
  productImages: EditImagesValues,
  values: EditImagesValues,
  onSavingComplete,
  onSavingError,
  retailerId: string,
  partnerId: string,
  productId: string
) => {
  try {
    const { data }: { data: ProductResponseBody } = await instacart.go.get(
      `/v1/partners/${partnerId}/warehouses/${retailerId}/products/${productId}`
    )

    const requestBody = getImageUpdateRequest(data, productImages, values)
    const deletedImageAngles = extractDeletedImageAnglesFromValues(productImages, values)

    await instacart.go.put<{}, ProductUpdateRequestBody>(
      `/v1/partners/${partnerId}/warehouses/${retailerId}/products/${productId}`,
      {
        data: requestBody,
      }
    )

    if (deletedImageAngles.length > 0) {
      await instacart.go.delete<{}, ProductImageDeleteBody>(
        `/v1/partners/${partnerId}/warehouses/${retailerId}/products/${productId}/image`,
        {
          data: { image_angles: deletedImageAngles },
        }
      )
    }

    onSavingComplete()
  } catch (e) {
    errors.captureWarning(e)
    onSavingError()
  }
}
